import {
  createDateRange,
  createOperationTimeBoundary,
  createDailyResetBoundary,
} from '../utils/dateUtils'
import moment from 'moment'

const removeUndefinedFromObject = (obj) => {
  let newObj = {}
  Object.keys(obj).forEach((key) => {
    const value = obj[key]
    if (value !== undefined) newObj[key] = value
  })
  return newObj
}

/**
 * Transform query parameters to use timezone-aware date ranges
 * Converts 'daily' and 'operationDaily' flags to explicit date/time parameters
 */
const transformQueryWithTimezone = (query) => {
  if (!query) return query

  const transformed = { ...query }

  // Handle 'daily' flag - convert to startDate/endDate for today in user's timezone
  // This filters by serviceDateTime field
  if (query.daily === true) {
    // For summary reports, use 3 AM to 3 AM boundary instead of midnight to midnight
    if (query.summaryReport === true) {
      const boundary = createDailyResetBoundary()
      transformed.startDate = boundary.start
      transformed.endDate = boundary.end
    } else {
      const dateRange = createDateRange(moment())
      transformed.startDate = dateRange.start
      transformed.endDate = dateRange.end
    }
    delete transformed.daily
  }

  // Handle 'operationDaily' flag - convert to serviceInfoInTime_gte/serviceInfoInTime_lte
  // This filters by serviceInfo.inTime field (check-in time) with 12-hour boundary
  if (query.operationDaily === true) {
    const timeBoundary = createOperationTimeBoundary(12)
    transformed.serviceInfoInTime_gte = timeBoundary.start
    transformed.serviceInfoInTime_lte = timeBoundary.end
    delete transformed.operationDaily
  }

  return transformed
}

export const createURL = (url, query) => {
  if (query) {
    // Apply timezone transformation before building URL
    const transformedQuery = transformQueryWithTimezone(query)
    const { page, limit, sort, order, ...rest } = transformedQuery
    return (
      url +
      '?' +
      new URLSearchParams(
        removeUndefinedFromObject({
          ...rest,
          _page: page,
          _limit: limit,
          _sort: sort,
          _order: order,
        }),
      )
    )
  }
  return url
}

export const createCRUDActions = (fetchId, url) => ({
  list: (query, options = {}) => {
    const _fetchId = options.fetchId ? options.fetchId : fetchId
    return {
      type: `FETCH_${_fetchId.toUpperCase()}_LIST`,
      meta: { fetch_id: _fetchId },
      $payload: {
        url: createURL(url, query),
      },
    }
  },

  get: (id, resultTransform) => ({
    type: `FETCH_${fetchId.toUpperCase()}_SINGLE`,
    meta: { fetch_id: fetchId, type: 'single', resultTransform },
    $payload: {
      url: `${url}/${id}`,
    },
  }),

  create: (resource, onResponse, resultTransform) => ({
    type: `CREATE_${fetchId.toUpperCase()}`,
    meta: { fetch_id: fetchId, type: 'create', resultTransform },
    $payload: {
      url: url,
      options: {
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify(resource),
      },
      onResponse,
    },
  }),

  update: (resource, onResponse, resultTransform) => ({
    type: `UPDATE_${fetchId.toUpperCase()}`,
    meta: { fetch_id: fetchId, type: 'update', resultTransform },
    $payload: {
      url: `${url}/${resource._id}`,
      options: {
        headers: { 'Content-Type': 'application/json' },
        method: 'PUT',
        body: JSON.stringify(resource),
      },
      onResponse,
    },
  }),

  delete: (_id, onResponse, resultTransform) => ({
    type: `DELETE_${fetchId.toUpperCase()}`,
    meta: { fetch_id: fetchId, type: 'delete', _id, resultTransform },
    $payload: {
      url: `${url}/${_id}`,
      options: {
        headers: { 'Content-Type': 'application/json' },
        method: 'DELETE',
      },
      responseType: 'text',
      onResponse,
    },
  }),

  clear: (type = 'single', options = {}) => ({
    type: 'CLEAR_FETCH_DATA',
    meta: { fetch_id: options.fetchId || fetchId, type },
  }),
})
